import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  Download,
  Eye,
  EyeOff,
  FileJson,
  FileSpreadsheet,
  Folder,
  FolderPlus,
  FolderClosed,
  FolderInput,
  Globe,
  HardDrive,
  Inbox,
  Pencil,
  Search,
  Send,
  Trash2,
  Upload,
} from 'lucide-react';
import { api, getAccessToken, unduhBerkas } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/States';
import { useToast } from '@/components/ui/Toast';
import { PublishDialog } from '@/components/storage/PublishDialog';
import { PreviewDialog } from '@/components/storage/PreviewDialog';
import { MoveDialog } from '@/components/storage/MoveDialog';
import { ReceiveDialog, SendDialog } from '@/components/storage/TransferDialogs';
import { useDebounced } from '@/hooks/useDebounced';
import { formatBytes, formatNumber, formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';

const FILTERS = [
  ['', 'Semua'],
  ['job', 'Hasil Scraping'],
  ['upload', 'Unggahan Saya'],
  ['dataset', 'Dari Data Publik'],
  ['script', 'Hasil Skrip'],
];

/**
 * Kapan sebuah berkas menyentuh masa retensinya.
 *
 * ── Kenapa perlu terlihat ───────────────────────────────────────────────
 *
 * Retensi menghapus berkas, dan sebelum ini tidak ada satu pun tanda di layar
 * bahwa itu akan terjadi. Yang menyimpan hasil scraping berbulan-bulan tidak
 * punya cara mengetahui mana yang akan hilang minggu depan, sampai ia membuka
 * folder dan menemukannya sudah tidak ada.
 *
 * Yang ditampilkan tanggalnya, bukan cuma "akan dihapus": tanggal bisa
 * ditindaklanjuti, peringatan tanpa tanggal tidak.
 */
const HARI_MS = 24 * 60 * 60 * 1000;

const sisaHari = (expiresAt) =>
  Math.ceil((new Date(expiresAt).getTime() - Date.now()) / HARI_MS);

const keteranganRetensi = (expiresAt) => {
  const sisa = sisaHari(expiresAt);
  const tanggal = new Date(expiresAt).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Jakarta',
  });

  if (sisa < 0) return `melewati retensi, menunggu disapu`;
  if (sisa === 0) return `dihapus hari ini`;
  return `dihapus ${tanggal}`;
};

/**
 * Lencana yang hanya muncul saat waktunya sudah dekat.
 *
 * Menandai seluruh berkas akan membuat penandanya jadi latar belakang yang
 * berhenti dibaca. Yang benar-benar perlu diperhatikan adalah yang tinggal
 * beberapa hari lagi.
 */
const LencanaRetensi = ({ expiresAt }) => {
  if (!expiresAt) return null;
  const sisa = sisaHari(expiresAt);
  if (sisa > 7) return null;

  return (
    <Badge tone={sisa <= 2 ? 'danger' : 'warning'} size="sm">
      {sisa < 0 ? 'Kedaluwarsa' : sisa === 0 ? 'Dihapus hari ini' : `${sisa} hari lagi`}
    </Badge>
  );
};

const SOURCE_LABEL = {
  job: 'Hasil Scraping',
  upload: 'Diunggah',
  dataset: 'Dari Data Publik',
  script: 'Hasil Skrip',
};

export const StoragePage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef(null);

  const [folderId, setFolderId] = useState(null);
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 350);

  const [newFolder, setNewFolder] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [moving, setMoving] = useState(null); // { type: 'file' | 'folder', item }
  const [renamingFile, setRenamingFile] = useState(null);
  const [terimaTerbuka, setTerimaTerbuka] = useState(false);
  const [kirimTerbuka, setKirimTerbuka] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [uploading, setUploading] = useState(null);

  const params = { limit: 200 };
  if (source) params.source = source;
  if (debouncedSearch) params.search = debouncedSearch;
  if (!source && !debouncedSearch && folderId) params.folderId = folderId;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['storage', { folderId, source, search: debouncedSearch }],
    queryFn: () => api.get('/storage/files', { params }),
  });

  const { data: usageData } = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.get('/dashboard/usage'),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['storage'] });
    queryClient.invalidateQueries({ queryKey: ['usage'] });
  };

  const deleteFile = useMutation({
    mutationFn: (id) => api.delete(`/storage/files/${id}`),
    onSuccess: () => {
      toast.success('Berkas dihapus');
      setPendingDelete(null);
      invalidate();
    },
    onError: (err) => toast.error('Gagal menghapus', { description: err.message }),
  });

  const deleteFolder = useMutation({
    mutationFn: (id) => api.delete(`/storage/folders/${id}`),
    onSuccess: (res) => {
      toast.success('Folder dihapus', {
        description: `${res.data.fileCount} berkas ikut terhapus.`,
      });
      setPendingDelete(null);
      invalidate();
    },
    onError: (err) => toast.error('Gagal menghapus folder', { description: err.message }),
  });

  const unpublish = useMutation({
    mutationFn: (id) => api.post(`/storage/files/${id}/unpublish`),
    onSuccess: () => {
      toast.success('Dataset ditarik dari publik');
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
    onError: (err) => toast.error('Gagal menarik dataset', { description: err.message }),
  });

  /**
   * Unggah berkas.
   *
   * Memakai XMLHttpRequest, bukan fetch, semata karena `upload.onprogress` - 
   * fetch belum punya padanannya, dan tanpa indikator kemajuan unggahan
   * berkas besar terasa seperti aplikasi yang menggantung.
   */
  const uploadFiles = async (list) => {
    const token = getAccessToken();
    const orgId = useAuthStore.getState().organization?.id;

    for (const file of Array.from(list)) {
      setUploading({ name: file.name, percent: 0 });

      try {
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', '/api/v1/storage/upload');
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.setRequestHeader('X-Organization-Id', orgId);
          xhr.setRequestHeader('X-Filename', encodeURIComponent(file.name));
          if (folderId) xhr.setRequestHeader('X-Folder-Id', folderId);
          xhr.setRequestHeader('Content-Type', 'application/octet-stream');

          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              setUploading({ name: file.name, percent: Math.round((e.loaded / e.total) * 100) });
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) return resolve();
            let pesan = 'Gagal mengunggah.';
            try {
              pesan = JSON.parse(xhr.responseText).error.message;
            } catch {
              /* respons bukan JSON - pakai pesan bawaan */
            }
            reject(new Error(pesan));
          };
          xhr.onerror = () => reject(new Error('Koneksi terputus saat mengunggah.'));
          xhr.send(file);
        });

        toast.success(`${file.name} terunggah`);
      } catch (err) {
        toast.error(`Gagal mengunggah ${file.name}`, { description: err.message });
      }
    }

    setUploading(null);
    invalidate();
    if (fileInput.current) fileInput.current.value = '';
  };

  const folders = data?.data?.folders ?? [];
  const files = data?.data?.files ?? [];
  const breadcrumb = data?.data?.breadcrumb ?? [];
  const storage = usageData?.data?.storage;
  const isBrowsing = !source && !debouncedSearch;
  const kosong = folders.length === 0 && files.length === 0;

  return (
    <div
      className="space-y-4"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
      }}
    >
      {/* Kuota */}
      <Card>
        <CardBody className="flex flex-wrap items-center gap-4">
          <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-[var(--surface-sunken)] text-[var(--text-muted)]">
            <HardDrive className="size-5" aria-hidden="true" />
          </div>
          <div className="min-w-[200px] flex-1">
            <div className="flex items-baseline justify-between">
              <p className="text-sm font-bold text-[var(--text-primary)]">Penyimpanan</p>
              <p className="text-sm tabular text-[var(--text-muted)]">
                {storage
                  ? `${formatBytes(storage.used)} dari ${formatBytes(storage.limit)}`
                  : ' - '}
              </p>
            </div>
            <div
              className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--surface-sunken)]"
              role="progressbar"
              aria-valuenow={storage?.percent ?? 0}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Pemakaian penyimpanan"
            >
              <div
                className={cn(
                  'h-full rounded-full transition-[width] duration-500',
                  (storage?.percent ?? 0) >= 90
                    ? 'bg-danger-500'
                    : (storage?.percent ?? 0) >= 70
                      ? 'bg-warning-500'
                      : 'bg-accent-600',
                )}
                style={{ width: `${Math.min(100, storage?.percent ?? 0)}%` }}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Aksi + pencarian */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari berkas…"
          iconLeft={Search}
          aria-label="Cari berkas"
          className="w-full sm:max-w-xs"
        />

        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setSource(value)}
              aria-pressed={source === value}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                source === value
                  ? 'border-brand-950 bg-brand-950 text-white dark:border-accent-600 dark:bg-accent-600'
                  : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--surface-raised)]',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="ml-auto flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={Inbox}
            onClick={() => setTerimaTerbuka(true)}
          >
            Terima
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={Send}
            onClick={() => setKirimTerbuka(true)}
          >
            Kirim
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={FolderPlus}
            onClick={() => setNewFolder(true)}
            disabled={!isBrowsing}
            title={isBrowsing ? undefined : 'Hapus filter dulu untuk membuat folder'}
          >
            Folder Baru
          </Button>
          <Button size="sm" icon={Upload} onClick={() => fileInput.current?.click()}>
            Unggah
          </Button>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept=".csv,.json,.ndjson,.xlsx"
            className="hidden"
            onChange={(e) => e.target.files?.length && uploadFiles(e.target.files)}
          />
        </div>
      </div>

      {/* Breadcrumb */}
      {isBrowsing && (
        <nav className="flex flex-wrap items-center gap-1 text-sm" aria-label="Lokasi folder">
          <button
            type="button"
            onClick={() => setFolderId(null)}
            className={cn(
              'rounded-md px-2 py-1 font-medium transition-colors hover:bg-[var(--surface-raised)]',
              folderId === null
                ? 'text-[var(--text-primary)]'
                : 'text-[var(--text-muted)]',
            )}
          >
            Penyimpanan
          </button>
          {breadcrumb.map((crumb, i) => (
            <span key={crumb.id} className="flex items-center gap-1">
              <ChevronRight className="size-3.5 text-[var(--text-muted)]" aria-hidden="true" />
              <button
                type="button"
                onClick={() => setFolderId(crumb.id)}
                className={cn(
                  'rounded-md px-2 py-1 font-medium transition-colors hover:bg-[var(--surface-raised)]',
                  i === breadcrumb.length - 1
                    ? 'text-[var(--text-primary)]'
                    : 'text-[var(--text-muted)]',
                )}
              >
                {crumb.name}
              </button>
            </span>
          ))}
        </nav>
      )}

      {uploading && (
        <Card>
          <CardBody className="flex items-center gap-3">
            <Upload className="size-4 shrink-0 text-accent-600" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[var(--text-primary)]">
                Mengunggah {uploading.name}
              </p>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[var(--surface-sunken)]">
                <div
                  className="h-full rounded-full bg-accent-600 transition-[width] duration-200"
                  style={{ width: `${uploading.percent}%` }}
                />
              </div>
            </div>
            <span className="shrink-0 text-sm tabular text-[var(--text-muted)]">
              {uploading.percent}%
            </span>
          </CardBody>
        </Card>
      )}

      <Card className="overflow-hidden">
        {isError ? (
          <ErrorState message={error?.message} onRetry={refetch} />
        ) : isLoading ? (
          <div className="divide-y divide-[var(--border-subtle)]">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <Skeleton className="size-9 rounded-lg" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        ) : kosong ? (
          <EmptyState
            icon={FolderClosed}
            title={
              debouncedSearch
                ? 'Tidak ada berkas yang cocok'
                : folderId
                  ? 'Folder ini kosong'
                  : 'Penyimpanan masih kosong'
            }
            description={
              debouncedSearch
                ? 'Coba kata kunci yang lain.'
                : 'Unggah berkas dari komputer, atau simpan hasil scraping ke sini lewat tombol “Simpan ke Cloud” di halaman detail job. Anda juga bisa menyeret berkas langsung ke halaman ini.'
            }
            action={
              !debouncedSearch && (
                <Button icon={Upload} onClick={() => fileInput.current?.click()}>
                  Unggah Berkas
                </Button>
              )
            }
          />
        ) : (
          <ul className="divide-y divide-[var(--border-subtle)]">
            {folders.map((folder) => (
              <li
                key={folder.id}
                className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-[var(--surface-sunken)]"
              >
                <button
                  type="button"
                  onClick={() => setFolderId(folder.id)}
                  className="flex min-w-0 flex-1 items-center gap-4 text-left"
                >
                  <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-accent-50 text-accent-600 dark:bg-accent-500/12 dark:text-accent-400">
                    <Folder className="size-[18px]" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[var(--text-primary)]">
                      {folder.name}
                    </p>
                    <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                      {folder.fileCount
                        ? `${formatNumber(folder.fileCount)} berkas · ${formatBytes(folder.sizeBytes)}`
                        : 'Kosong'}
                    </p>
                  </div>
                </button>

                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="xs"
                    iconOnly
                    aria-label={`Pindahkan folder ${folder.name}`}
                    title="Pindahkan"
                    onClick={() => setMoving({ type: 'folder', item: folder })}
                  >
                    <FolderInput className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    iconOnly
                    aria-label={`Ganti nama ${folder.name}`}
                    onClick={() => setRenaming(folder)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    iconOnly
                    aria-label={`Hapus folder ${folder.name}`}
                    onClick={() => setPendingDelete({ type: 'folder', item: folder })}
                    className="hover:text-danger-600"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </li>
            ))}

            {files.map((file) => {
              const isJson = file.format === 'json' || file.format === 'ndjson';
              const Icon = isJson ? FileJson : FileSpreadsheet;

              return (
                <li
                  key={file.id}
                  className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-[var(--surface-sunken)]"
                >
                  <div
                    className={cn(
                      'grid size-9 shrink-0 place-items-center rounded-lg',
                      isJson
                        ? 'bg-warning-50 text-warning-600 dark:bg-warning-500/12 dark:text-warning-500'
                        : 'bg-success-50 text-success-600 dark:bg-success-500/12 dark:text-success-500',
                    )}
                  >
                    <Icon className="size-[18px]" aria-hidden="true" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-mono text-sm font-semibold text-[var(--text-primary)]">
                        {file.filename}
                      </p>
                      {file.published && (
                        <Badge tone="success" size="sm" dot>
                          Publik
                        </Badge>
                      )}
                      <LencanaRetensi expiresAt={file.expiresAt} />
                    </div>
                    <p className="mt-0.5 truncate text-xs text-[var(--text-muted)]">
                      {SOURCE_LABEL[file.source] ?? file.source} · {formatBytes(file.sizeBytes)}
                      {file.rowCount != null && ` · ${formatNumber(file.rowCount)} baris`}
                      {file.published && file.dataset && ` · dilihat ${formatNumber(file.dataset.views)}×`}
                      {file.expiresAt && ` · ${keteranganRetensi(file.expiresAt)}`}
                    </p>
                  </div>

                  <p className="hidden shrink-0 text-sm text-[var(--text-muted)] sm:block">
                    {formatRelative(file.createdAt)}
                  </p>

                  <div className="flex shrink-0 gap-1">
                    {file.source !== 'dataset' &&
                      (file.published ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          icon={EyeOff}
                          onClick={() => unpublish.mutate(file.id)}
                          loading={unpublish.isPending && unpublish.variables === file.id}
                        >
                          Tarik
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="xs"
                          icon={Globe}
                          onClick={() => setPublishing(file)}
                        >
                          Publish
                        </Button>
                      ))}

                    <Button
                      variant="ghost"
                      size="xs"
                      iconOnly
                      aria-label={`Pratinjau ${file.filename}`}
                      title="Pratinjau isi"
                      onClick={() => setPreviewing(file)}
                    >
                      <Eye className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      iconOnly
                      aria-label={`Ganti nama ${file.filename}`}
                      title="Ganti nama"
                      onClick={() => setRenamingFile(file)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      iconOnly
                      aria-label={`Pindahkan ${file.filename}`}
                      title="Pindahkan ke folder"
                      onClick={() => setMoving({ type: 'file', item: file })}
                    >
                      <FolderInput className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      iconOnly
                      aria-label={`Unduh ${file.filename}`}
                      onClick={() =>
                        unduhBerkas(`/storage/files/${file.id}/download`, file.filename).catch(
                          (err) => toast.error('Unduhan gagal', { description: err.message }),
                        )
                      }
                    >
                      <Download className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      iconOnly
                      aria-label={`Hapus ${file.filename}`}
                      onClick={() => setPendingDelete({ type: 'file', item: file })}
                      className="hover:text-danger-600"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <FolderDialog
        open={newFolder}
        onClose={() => setNewFolder(false)}
        parentId={folderId}
        onDone={invalidate}
      />

      <FolderDialog
        open={Boolean(renaming)}
        onClose={() => setRenaming(null)}
        folder={renaming}
        onDone={invalidate}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() =>
          pendingDelete.type === 'folder'
            ? deleteFolder.mutate(pendingDelete.item.id)
            : deleteFile.mutate(pendingDelete.item.id)
        }
        loading={deleteFile.isPending || deleteFolder.isPending}
        destructive
        title={pendingDelete?.type === 'folder' ? 'Hapus folder ini?' : 'Hapus berkas ini?'}
        description={
          pendingDelete?.type === 'folder'
            ? `"${pendingDelete.item.name}" akan dihapus beserta seluruh isinya${
                pendingDelete.item.fileCount
                  ? ` - termasuk ${pendingDelete.item.fileCount} berkas di dalamnya`
                  : ''
              }. Tindakan ini tidak bisa dibatalkan.`
            : pendingDelete?.item?.published
              ? `"${pendingDelete.item.filename}" sedang dipublikasikan. Menghapusnya juga menarik dataset dari Data Publik.`
              : `"${pendingDelete?.item?.filename}" akan dihapus permanen.`
        }
        confirmLabel="Ya, hapus"
      />

      {previewing && <PreviewDialog file={previewing} onClose={() => setPreviewing(null)} />}
      {moving && <MoveDialog target={moving} onClose={() => setMoving(null)} />}
      {renamingFile && (
        <RenameFileDialog file={renamingFile} onClose={() => setRenamingFile(null)} onDone={invalidate} />
      )}
      <ReceiveDialog open={terimaTerbuka} onClose={() => setTerimaTerbuka(false)} />
      <SendDialog open={kirimTerbuka} onClose={() => setKirimTerbuka(false)} />

      <PublishDialog
        open={Boolean(publishing)}
        onClose={() => setPublishing(null)}
        file={publishing}
      />
    </div>
  );
};

/** Dialog buat folder baru, atau ganti nama folder yang sudah ada. */
const FolderDialog = ({ open, onClose, parentId, folder, onDone }) => {
  const toast = useToast();
  const [name, setName] = useState('');
  const isRename = Boolean(folder);

  const mutation = useMutation({
    mutationFn: () =>
      isRename
        ? api.patch(`/storage/folders/${folder.id}`, { name: name.trim() })
        : api.post('/storage/folders', { name: name.trim(), parentId }),
    onSuccess: () => {
      toast.success(isRename ? 'Nama folder diubah' : 'Folder dibuat');
      onDone();
      onClose();
    },
    onError: (err) =>
      toast.error(isRename ? 'Gagal mengganti nama' : 'Gagal membuat folder', {
        description: err.message,
      }),
  });

  // Mengisi nama saat dialog dibuka, bukan saat render - kalau tidak, ketikan
  // pengguna akan tertimpa setiap kali komponen dirender ulang.
  const [lastOpen, setLastOpen] = useState(false);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setName(folder?.name ?? '');
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isRename ? 'Ganti Nama Folder' : 'Folder Baru'}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Batal
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!name.trim()}
          >
            {isRename ? 'Simpan' : 'Buat'}
          </Button>
        </>
      }
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && name.trim() && mutation.mutate()}
        placeholder="Misal: Riset Kompetitor"
        aria-label="Nama folder"
        data-autofocus
      />
      <p className="mt-2 text-xs text-[var(--text-muted)]">
        Garis miring akan diganti tanda hubung - folder dipisahkan lewat struktur, bukan nama.
      </p>
    </Modal>
  );
};

/** Dialog kecil untuk mengganti nama tampilan sebuah berkas. */
const RenameFileDialog = ({ file, onClose, onDone }) => {
  const toast = useToast();
  const [nama, setNama] = useState(file.filename.replace(/\.[^.]+$/, ''));

  const simpan = useMutation({
    mutationFn: () => api.patch(`/storage/files/${file.id}`, { filename: nama }),
    onSuccess: (res) => {
      toast.success('Nama diubah', { description: res?.data?.filename });
      onDone();
      onClose();
    },
    onError: (err) => toast.error('Gagal mengganti nama', { description: err?.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Ganti Nama Berkas"
      description={`Ekstensi .${file.format} dipertahankan otomatis.`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button loading={simpan.isPending} disabled={!nama.trim()} onClick={() => simpan.mutate()}>
            Simpan
          </Button>
        </div>
      }
    >
      <Input
        value={nama}
        onChange={(e) => setNama(e.target.value)}
        aria-label="Nama berkas baru"
        autoFocus
        className="h-10 w-full px-3 text-sm"
      />
    </Modal>
  );
};
